/**
 * EF-CLIENT-PORTAL-UI-V1
 * Client Shifts List Page
 * EF-CLIENT-PORTAL-V1-STEP3: Added shift detail modal
 * 
 * Features:
 * - Paginated shift list from API
 * - Server-side pagination and filtering
 * - Shows assigned crew per shift
 * - Shift detail modal with notes and meeting point
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Calendar,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Clock,
  MapPin,
  Users,
  User,
  Filter,
  Navigation,
  Info,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import ShiftDetailModal from "../../components/client/ShiftDetailModal";
import { useI18n } from "../../i18n/I18nProvider";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ClientShiftsPage() {
  const navigate = useNavigate();
  const { getToken } = useOutletContext();
  const { t } = useI18n();

  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [upcomingOnly, setUpcomingOnly] = useState("all");
  // EF-CLIENT-PORTAL-V1-STEP3: Shift detail modal state
  const [selectedShift, setSelectedShift] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchShifts = useCallback(async (page = 1, upcoming = "all") => {
    const token = getToken();
    if (!token) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: "20",
      });
      if (upcoming === "upcoming") {
        params.append("upcoming_only", "true");
      }

      const response = await fetch(`${API_URL}/api/client/shifts?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch shifts");

      const data = await response.json();
      setShifts(data.data || []);
      setMeta(data.meta || { page: 1, limit: 20, total: 0, total_pages: 0 });
    } catch (err) {
      console.error("Shifts fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchShifts(1, upcomingOnly);
  }, [fetchShifts, upcomingOnly]);

  const handlePageChange = (newPage) => {
    fetchShifts(newPage, upcomingOnly);
  };

  // EF-CLIENT-PORTAL-UI-REFINEMENT-V1: Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return date.toLocaleDateString("en-GB", { 
      weekday: "long", 
      day: "numeric", 
      month: "long" 
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "confirmed":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "draft":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
      case "completed":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "cancelled":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold" data-testid="shifts-title">{t("clientPortal:shiftsPage.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {meta.total} shift{meta.total !== 1 ? "s" : ""} total
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={upcomingOnly} onValueChange={setUpcomingOnly}>
            <SelectTrigger className="w-[150px]" data-testid="shift-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("clientPortal:shiftsPage.allShifts")}</SelectItem>
              <SelectItem value="upcoming">Upcoming Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : shifts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t("clientPortal:shiftsPage.noShiftsFound")}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* EF-CLIENT-PORTAL-UI-REFINEMENT-V1: Better spacing and date grouping */}
          <div className="space-y-2">
            {shifts.map((shift, index) => {
              // Check if we need a date header (new date group)
              const prevShift = shifts[index - 1];
              const showDateHeader = !prevShift || prevShift.date !== shift.date;
              
              return (
                <div key={shift.id}>
                  {/* Date Header */}
                  {showDateHeader && (
                    <div className="sticky top-14 z-10 bg-background py-2 -mx-4 px-4 md:-mx-6 md:px-6">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Calendar className="h-4 w-4 text-primary" />
                        <span>{formatDate(shift.date)}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* EF-CLIENT-PORTAL-V1-STEP3: Clickable shift card with meeting point */}
                  <Card
                    className="hover:bg-accent/30 transition-colors cursor-pointer"
                    data-testid={`shift-card-${shift.id}`}
                    onClick={() => {
                      setSelectedShift(shift);
                      setModalOpen(true);
                    }}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-start gap-4">
                        {/* Time block */}
                        <div className="shrink-0 w-16 text-center">
                          <div className="text-sm font-semibold">{shift.start_time}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {shift.duration_hours ? `${shift.duration_hours}h` : `– ${shift.end_time}`}
                          </div>
                        </div>
                        
                        {/* Shift details */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold truncate">{shift.name}</h3>
                            <Badge variant="outline" className={`text-[10px] ${getStatusColor(shift.status)}`}>
                              {shift.status}
                            </Badge>
                            {/* Detail indicator */}
                            <Info className="h-3.5 w-3.5 text-muted-foreground/50" />
                          </div>
                          
                          <p
                            className="text-xs text-primary cursor-pointer hover:underline mb-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/client/projects/${shift.project_id}`);
                            }}
                          >
                            {shift.project_name}
                          </p>

                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            {shift.venue_name && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {shift.venue_name}
                              </span>
                            )}
                            {/* EF-CLIENT-PORTAL-V1-STEP3: Show meeting point */}
                            {shift.venue_location_name && (
                              <span className="flex items-center gap-1 text-primary">
                                <Navigation className="h-3 w-3" />
                                {shift.venue_location_name}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {shift.staff_assigned || 0}/{shift.staff_required || 0} crew
                            </span>
                          </div>

                          {/* Assigned Crew - compact pills */}
                          {shift.assigned_crew?.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                              {shift.assigned_crew.slice(0, 4).map((crew) => (
                                <span
                                  key={crew.id}
                                  className="inline-flex items-center gap-1 text-[10px] bg-accent px-2 py-0.5 rounded-full"
                                >
                                  <User className="h-2.5 w-2.5" />
                                  {crew.name}
                                  {crew.role && <span className="text-muted-foreground">• {crew.role}</span>}
                                </span>
                              ))}
                              {shift.assigned_crew.length > 4 && (
                                <span className="text-[10px] text-muted-foreground px-2 py-0.5">
                                  +{shift.assigned_crew.length - 4} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {meta.total_pages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                Page {meta.page} of {meta.total_pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page <= 1}
                  onClick={() => handlePageChange(meta.page - 1)}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page >= meta.total_pages}
                  onClick={() => handlePageChange(meta.page + 1)}
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* EF-CLIENT-PORTAL-V1-STEP3: Shift Detail Modal */}
      <ShiftDetailModal
        shift={selectedShift}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </div>
  );
}
